import { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { usePageContent, useSiteData } from '@/context/SiteDataContext';
import EditText from '@/components/editable/EditText';
import PageHero from '@/components/PageHero';

export default function Contact() {
  const c = usePageContent('contact');
  const { settings = {} } = useSiteData();
  const s = settings || {};
  const [form, setForm] = useState({ name: '', email: '', subject: '', department: '', message: '' });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/contact', form);
      toast.success('Message sent! We will be in touch shortly.');
      setForm({ name: '', email: '', subject: '', department: '', message: '' });
    } catch {
      toast.error('Could not send message. Please try again.');
    }
  };
  return (
    <div>
      <PageHero slug="contact" overline="We're Here" heading="Contact Us" text="Get in touch with the jurisdictional office — we welcome your questions, prayer requests, and ministry inquiries." />
      <section className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12">
          <div>
            <EditText field="form_heading" target="page:contact" as="h2" className="font-serif text-3xl text-royal font-bold mb-6 gold-underline">{c.form_heading || 'Get in Touch'}</EditText>
            <div className="space-y-5 text-charcoal-muted">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gold mt-1" />
                <div>
                  <div className="font-semibold text-royal">Jurisdictional Office</div>
                  <EditText field="address_line1" target="settings" as="span" className="block">{s.address_line1 || '2020 W. Fond du Lac Ave'}</EditText>
                  <EditText field="address_line2" target="settings" as="span" className="block">{s.address_line2 || 'Milwaukee, WI 53206'}</EditText>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-gold mt-1" />
                <div>
                  <div className="font-semibold text-royal">Phone</div>
                  <EditText field="phone" target="settings">{s.phone || '(414) 555-0100'}</EditText>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-gold mt-1" />
                <div>
                  <div className="font-semibold text-royal">Email</div>
                  <EditText field="email" target="settings">{s.email || 'info@wifirstjurisdiction.org'}</EditText>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-gold mt-1" />
                <div>
                  <div className="font-semibold text-royal">Office Hours</div>
                  <EditText field="office_hours" target="settings">{s.office_hours || 'Mon–Fri · 9:00 AM – 5:00 PM CT'}</EditText>
                </div>
              </div>
            </div>
            <div className="mt-8 rounded-2xl overflow-hidden shadow-lg">
              <iframe width="100%" height="300" loading="lazy" src={`https://maps.google.com/maps?q=${encodeURIComponent((s.address_line1 || '2020 W. Fond du Lac Ave') + ', ' + (s.address_line2 || 'Milwaukee, WI 53206'))}&z=14&output=embed`} title="Office Map" />
            </div>
          </div>
          <form onSubmit={submit} className="card-tactile p-8 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-royal mb-1">Name</label>
              <input data-testid="contact-name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-royal/40" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-royal mb-1">Email</label>
              <input data-testid="contact-email" required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-royal/40" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-royal mb-1">Subject</label>
              <input data-testid="contact-subject" required value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-royal/40" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-royal mb-1">Department (optional)</label>
              <input data-testid="contact-department" value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-royal/40" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-royal mb-1">Message</label>
              <textarea data-testid="contact-message" required rows="5" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-royal/40" />
            </div>
            <button data-testid="contact-submit-btn" type="submit" className="btn-royal w-full justify-center">Send Message</button>
          </form>
        </div>
      </section>
    </div>
  );
}
